'use client';

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import { defaultVariants } from './Accordion.styles';
import { Container } from '@/components/primitives/Container/Container';
import SvgIcon from '@/helpers/SvgIcon/SvgIcon';
import { getCSLPAttributes } from '@/utils/type-guards';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IStandardContentPageComponents } from '@/.generated';
import { AccordionItem } from './AccordionItem';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { trackAdobeClick } from '@/utils/adobe/click';
import { JsonLdScript } from '@/helpers/Seo/JsonLdScript';

type AccordionProps = IStandardContentPageComponents['accordion'] &
  WithMetadata<IBaseComponentProps>;

type AccordionFaqItem = NonNullable<AccordionProps['accordion_items']>[number];

// Builds a schema.org FAQPage payload from accordion question/answer items.
const buildFaqPageSchema = (items: AccordionFaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.title,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.description,
    },
  })),
});

const Default = (props: AccordionProps) => {
  const {
    accordion_items,
    enable_expand_all,
    expand_first_item,
    _metadata,
    componentName,
    hide_component,
  } = props;
  const [openItemUids, setOpenItemUids] = useState<Set<string>>(
    expand_first_item && accordion_items && accordion_items.length > 0
      ? new Set(['accordion-item-0'])
      : new Set()
  );
  const {
    globalLabels: { expand_all_label, collapse_all_label },
  } = useGlobalLabels();

  const containerRef = useRef<HTMLDivElement>(null);

  const allExpanded =
    accordion_items && accordion_items.length > 0 && openItemUids.size === accordion_items.length;

  const isCollapseMode = openItemUids.size > 1;

  const { base, header, expandAllButton, expandAllChevron, itemList } = defaultVariants({
    allExpanded,
  });

  const toggleItem = useCallback(
    (uid: string, event?: MouseEvent<HTMLElement>) => {
      const button = containerRef.current?.querySelector<HTMLElement>(
        `[data-accordion-uid="${uid}"]`
      );

      // Derive the original item index from the auto-generated uid (e.g. "accordion-item-3")
      const itemIndex = Number(uid.split('-').pop());
      const itemTitle = accordion_items?.[itemIndex]?.title ?? uid;
      trackAdobeClick({ clickName: itemTitle, clickUrl: 'accordion-view' }, event ?? button ?? null);

      setOpenItemUids((prev) =>
        prev.has(uid) ? new Set([...prev].filter((id) => id !== uid)) : new Set([uid])
      );
    },
    [accordion_items]
  );

  const toggleAll = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      trackAdobeClick(
        {
          clickName: isCollapseMode
            ? (collapse_all_label ?? 'Collapse All')
            : (expand_all_label ?? 'Expand All'),
          clickUrl: 'accordion-view',
        },
        event
      );
      if (isCollapseMode) {
        setOpenItemUids(new Set());
      } else {
        setOpenItemUids(
          new Set(accordion_items?.map((_, index) => `accordion-item-${index}`) ?? [])
        );
      }
    },
    [isCollapseMode, accordion_items, collapse_all_label, expand_all_label]
  );

  if (hide_component) return null;

  if (!accordion_items || accordion_items.length === 0) return <></>;

  const faqItems = accordion_items.filter((item) => item.title && item.description);

  return (
    <>
      {faqItems.length > 0 && <JsonLdScript schema={buildFaqPageSchema(faqItems)} />}
      <Container
        componentName={componentName}
        metaId={_metadata?.uid}
        id={props.inline_navigation_id}
        horizontalPadding
      >
        <div ref={containerRef} className={base()}>
          {enable_expand_all && (
            <div className={header()}>
              <button
                type="button"
                className={expandAllButton()}
                onClick={toggleAll}
                aria-expanded={isCollapseMode}
                {...getCSLPAttributes(isCollapseMode ? collapse_all_label : expand_all_label)}
              >
                {isCollapseMode ? collapse_all_label : expand_all_label}
                <SvgIcon
                  icon="chevron-down"
                  size="xs"
                  fill="currentColor"
                  viewBox="1 6.5 22 11"
                  className={expandAllChevron()}
                />
              </button>
            </div>
          )}
          <ul className={itemList()}>
            {accordion_items?.map((item, index) => (
              <AccordionItem
                key={index}
                {...item}
                uid={`accordion-item-${index}`}
                extendedProps={{ openItemUids, onToggle: toggleItem }}
              />
            ))}
          </ul>
        </div>
      </Container>
    </>
  );
};

const variants = {
  Default,
};

export const Accordion = (props: AccordionProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
